import React, { useState } from "react";
import { Box, Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { TransportOrderInvoiceSumRow } from "./TransportOrderInvoiceSumRow";
import { apiCall } from "../../../services/apiClient";
import { showApiError, showApiSuccess } from "../../framework/formUtils";
import { RootState, useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { fetchTransportOrderInvoice } from "../../../store/transportOrderInvoiceSlice";
import { useParams } from "react-router-dom";
import { TransportOrderInvoiceRowDialog } from "./TransportOrderInvoiceRowDialog";
import { useTranslation } from "react-i18next";
import { InvoiceRowType } from "../../../models/invoice/invoiceRowType";
import { TransportOrderInvoiceRowDto } from "../../../models/transportOrderInvoice/TransportOrderInvoiceRowDto";
import { TransportTaskType } from "../../../models/transport/transportTaskType";
import { getDateTime } from "../components/transportUtils";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import * as storeActions from "../../../models/store/storeActions";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";
import { removeInvoiceRow } from "../../../services/newInvoiceService";
import { TransportOrderInvoiceNewRowDialog } from "./TransportOrderInvoiceNewRowDialog";
import { InvoiceRowListItemDto } from "../../../models/transportOrderInvoice/invoiceRowListItemDto";

interface ITransportOrderInvoiceDetailsProps {
    editEnabled?: boolean;
    selectedCustomer?: string;
}

export const TransportOrderInvoiceDetails = ({ editEnabled, selectedCustomer }: ITransportOrderInvoiceDetailsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const titleColor = { color: theme.palette.primary.dark };
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const [showAddRowDialog, setShowAddRowDialog] = useState<boolean>(false);
    const [showInvoiceRowDialog, setShowInvoiceRowDialog] = useState<boolean>(false);
    const selectedInvoice = useAppSelector((state: RootState) => state.transportOrderInvoice?.selectedInvoice);
    const transportOrderRows = selectedInvoice?.transportOrderRows || [];
    const invoiceCustomer = selectedInvoice?.customerId || selectedCustomer;
    const [selectedTransportOrderInvoice, setSelectedTransportOrderInvoice] = useState<TransportOrderInvoiceRowDto>(null);
    const [selectedInvoiceRow, setSelectedInvoiceRow] = useState<InvoiceRowListItemDto>(null);

    const handleRemoveOrderFromInvoice = (orderId: string) => {
        const submitRemove = () => {
            apiCall(`TransportOrders/${orderId}/InvoiceRows`, "DELETE")
                .then(() => {
                    showApiSuccess(t("common.deleteWasSuccess"));
                    dispatch(fetchTransportOrderInvoice(id) as any);
                })
                .catch((error) => {
                    showApiError(error.message);
                });
        };

        dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.areYouSureWantDelete"),
                t("transport.invoicing.deleteOrderFromInvoiceInfo"),
                submitRemove)
        );
    };

    const handleAddNewRowClick = () => {
        setShowAddRowDialog(true);
    };

    const handleClose = () => {
        setSelectedTransportOrderInvoice(null);
        setSelectedInvoiceRow(null);
        setShowAddRowDialog(false);
        setShowInvoiceRowDialog(false);
    };

    const handleInvoiceRowCreate = (transportOrder: TransportOrderInvoiceRowDto) => {
        setSelectedTransportOrderInvoice(transportOrder);
        setShowInvoiceRowDialog(true);
    }

    const handleInvoiceRowUpdate = (transportOrder: TransportOrderInvoiceRowDto, invoiceRow: InvoiceRowListItemDto) => {
        setSelectedTransportOrderInvoice(transportOrder);
        setSelectedInvoiceRow(invoiceRow);
        setShowInvoiceRowDialog(true);
    }

    const handleDelete = (invoiceRowId) => {
        dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("transport.invoicing.deleteInvoiceRowConfirmation"),
                () => {
                    removeInvoiceRow(invoiceRowId)
                        .then(() => {
                            showApiSuccess(t("transport.invoicing.invoiceRowRemoved"));
                            dispatch(fetchTransportOrderInvoice(id) as any);
                        })
                        .catch((error) => {
                            showApiError(error.message);
                        });
                }
            )
        );
    };

    const getOrderTitle = (order: TransportOrderInvoiceRowDto) => {
        const titleStart = t("common.order") + " " + order.orderNumber;
        const deliveryDate = getDeliveryDate(order);
        let titleEnd = "";
        if (deliveryDate) {
            titleEnd = " - " + t("transport.order.deliveryDate") + " " + deliveryDate;
        }

        if (order.name) {
            return titleStart + " - " + order.name + titleEnd;
        }
        return titleStart + titleEnd;
    };

    const getDeliveryDate = (order: TransportOrderInvoiceRowDto) => {
        if (order.latestTransportTaskType === TransportTaskType.Delivered.valueOf() &&
            order.latestTransportTaskTypeChangedDateTime
        ) {
            return getDateTime(order.latestTransportTaskTypeChangedDateTime);
        }
        return null;
    };

    return (
        <>
            <Box style={{ display: "flex", justifyContent: "space-between" }} >
                <div />
                {(editEnabled || id === "new") &&
                    <Button onClick={handleAddNewRowClick} variant="contained" color="primary" style={{ marginBottom: 30 }}>
                        {t("transport.invoicing.addTransportToInvoice")}
                    </Button>}
            </Box>
            {showAddRowDialog &&
                <TransportOrderInvoiceRowDialog
                    title={t("transport.invoicing.addTransportToInvoice")}
                    open={showAddRowDialog}
                    onClose={handleClose}
                    selectedInvoiceCustomerId={invoiceCustomer}
                />
            }
            {showInvoiceRowDialog &&
                <TransportOrderInvoiceNewRowDialog
                    open={showInvoiceRowDialog}
                    onClose={handleClose}
                    transportOrderInvoice={selectedTransportOrderInvoice}
                    invoiceRow={selectedInvoiceRow}
                />
            }

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell size={"small"}><Typography variant="subtitle1" fontWeight={"bold"} color="success.main">{t("transport.orders")}</Typography></TableCell>
                        <TableCell size={"small"} style={titleColor}>{t("transport.parcel.amount")}</TableCell>
                        <TableCell size={"small"} style={titleColor}>{t("transport.parcel.type")}</TableCell>
                        <TableCell size={"small"} style={titleColor}>{t("transport.parcel.unitPriceEUR")}</TableCell>
                        <TableCell size={"small"} style={titleColor}>{t("transport.invoicing.taxFree")}</TableCell>
                        <TableCell size={"small"} style={titleColor}>{t("transport.invoicing.taxInclusive")}</TableCell>
                        <TableCell size={"small"} style={titleColor}>{t("common.vatRate")} %</TableCell>
                    </TableRow>
                </TableHead>
                {transportOrderRows.length === 0 ? (
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={9}>
                                <i>{t("transport.invoicing.noInvoiceRows")}</i>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                ) : (
                    <TableBody>
                        {transportOrderRows.map((transportOrder) => (
                            <React.Fragment key={transportOrder.orderNumber}>
                                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                                    <TableCell size={"small"}>
                                        <Typography variant={"subtitle1"} color="primary.dark" fontWeight="bold">{getOrderTitle(transportOrder)}</Typography>
                                    </TableCell>
                                    <TableCell colSpan={7} size={"small"} align="right">
                                        {editEnabled &&
                                            <>
                                                <Button onClick={() => handleInvoiceRowCreate(transportOrder)}>
                                                    {t("transport.invoicing.addRow")}
                                                </Button>
                                                <Button onClick={() => handleRemoveOrderFromInvoice(transportOrder?.orderId)}>
                                                    {t("transport.order.removeOrder")}
                                                </Button>
                                            </>
                                        }
                                    </TableCell>
                                </TableRow>

                                {transportOrder.rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.rowType === InvoiceRowType.Product &&
                                            <>
                                                <TableCell size={"small"} style={{ fontWeight: row.description === t("transport.order.fixedPrice") ? "bold" : "normal" }}>{
                                                    row.description}
                                                </TableCell>
                                                <TableCell size={"small"}>{row.amount}</TableCell>
                                                <TableCell size={"small"}>{t("common.measureUnitPcs")}</TableCell>
                                                <TableCell size={"small"}>{row.unitPrice?.toFixed(2)}</TableCell>
                                                <TableCell size={"small"}>{(row.amount * row.unitPrice)?.toFixed(2)}</TableCell>
                                                <TableCell size={"small"}>{(row.amount * row.unitPrice * (1 + row.vat / 100))?.toFixed(2)}</TableCell>
                                                <TableCell size={"small"}>{`${row.vat?.toFixed(2)} %`}</TableCell>
                                            </>}
                                        {row.rowType === InvoiceRowType.Text &&
                                            <>
                                                <TableCell size={"small"} colSpan={6}>{row.description}</TableCell>
                                                <TableCell size={"small"}>
                                                    {editEnabled &&
                                                        <Grid2 container justifyContent="flex-end">
                                                            <IconButton size="small" onClick={() => {handleInvoiceRowUpdate(transportOrder, row)}}>
                                                                <EditIcon color="primary" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleDelete(row.id)}>
                                                                <DeleteIcon color="error" />
                                                            </IconButton>
                                                        </Grid2>
                                                    }
                                                </TableCell>
                                            </>}
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        ))}
                    </TableBody>
                )}
            </Table>

            {transportOrderRows.length > 0 && id !== "new" &&
                <div style={{ marginTop: "40px" }}>
                    <TransportOrderInvoiceSumRow title={`${t("common.total")} EUR`} value={selectedInvoice.totalVatExclusiveSum} />
                    <TransportOrderInvoiceSumRow title={t("common.vatRate")} value={selectedInvoice.totalVatSum} />
                    <TransportOrderInvoiceSumRow title={t("transport.invoicing.taxInclusiveTotalEUR")} value={selectedInvoice.totalVatInclusiveSum} />
                </div>
            }
        </>
    );
};
